import { IPublicClientApplication } from '@azure/msal-browser';
import { getGraphHeaders } from '../auth/authService';

const GRAPH = 'https://graph.microsoft.com/v1.0';

export async function graphGet<T>(
  pca: IPublicClientApplication,
  path: string,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers = { ...(await getGraphHeaders(pca)), ...(extraHeaders ?? {}) };
  const res = await fetch(`${GRAPH}${path}`, { headers });
  if (!res.ok) {
    // Surface the Graph error payload — callers show it to the user, so
    // "403 Missing scope" must not collapse into a bare status code.
    const text = await res.text().catch(() => '');
    let detail = '';
    try { detail = (JSON.parse(text) as { error?: { message?: string } }).error?.message ?? ''; }
    catch { detail = text.slice(0, 300); }
    const err = new Error(`graphGet ${path} failed: ${res.status}${detail ? ` — ${detail}` : ''}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

export async function graphPost<T>(
  pca: IPublicClientApplication,
  path: string,
  body: unknown
): Promise<T> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch(`${GRAPH}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`graphPost ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

/**
 * POST that expects no response body (e.g. /me/sendMail returns 202
 * Accepted with an empty body). Throws with the status on failure.
 */
export async function graphPostVoid(
  pca: IPublicClientApplication,
  path: string,
  body: unknown,
): Promise<void> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch(`${GRAPH}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = new Error(`graphPostVoid ${path} failed: ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
}

/**
 * JSON PUT (e.g. PUT /groups/{id}/team to add Teams capability). Returns the
 * parsed body when present, otherwise an empty object.
 */
export async function graphPutJson<T>(
  pca: IPublicClientApplication,
  path: string,
  body: unknown,
): Promise<T> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch(`${GRAPH}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`graphPutJson ${path} failed: ${res.status} ${text.slice(0, 300)}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  const raw = await res.text().catch(() => '');
  return (raw ? JSON.parse(raw) : {}) as T;
}

/** DELETE (e.g. remove a DriveItem). Status is surfaced on the thrown error. */
export async function graphDelete(
  pca: IPublicClientApplication,
  path: string,
): Promise<void> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch(`${GRAPH}${path}`, { method: 'DELETE', headers });
  // 204 No Content on success; 404 is treated as already-gone (idempotent).
  if (!res.ok && res.status !== 404) {
    const err = new Error(`graphDelete ${path} failed: ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
}

/**
 * Raw PUT for binary content (e.g. DriveItem upload via
 * /drive/root:/{path}:/content). Reuses the Graph auth header but
 * overrides Content-Type for the binary body. Status is surfaced on the
 * thrown error so callers can distinguish 401/403 (access) from others.
 */
export async function graphPut<T>(
  pca: IPublicClientApplication,
  path: string,
  body: ArrayBuffer | Blob,
  contentType = 'application/octet-stream',
): Promise<T> {
  const auth = await getGraphHeaders(pca);
  const res = await fetch(`${GRAPH}${path}`, {
    method: 'PUT',
    headers: { Authorization: auth.Authorization, 'Content-Type': contentType },
    body,
  });
  if (!res.ok) {
    const err = new Error(`graphPut ${path} failed: ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}
